'use client';

import React, { useState, useCallback, useRef } from 'react';
import './CopyButton.css';

export interface CopyButtonProps extends Omit<React.HTMLAttributes<HTMLButtonElement>, 'onCopy'> {
  /** Text to copy to clipboard */
  text: string;
  /** Tooltip label (default: "Copy") */
  label?: string;
  /** Message shown in the confirmation popup above the button after a copy (default: "Copied to clipboard") */
  copiedMessage?: string;
  /** Custom trigger content (replaces default icon) */
  children?: React.ReactNode;
  /** Called after successful copy */
  onCopy?: (text: string) => void;
}

export const CopyButton: React.FC<CopyButtonProps> = ({
  text,
  label = 'Copy',
  copiedMessage = 'Copied to clipboard',
  children,
  onCopy,
  className = '',
  ...rest
}) => {
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      onCopy?.(text);

      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      onCopy?.(text);

      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => setCopied(false), 2000);
    }
  }, [text, onCopy]);

  const classes = ['hi-ui-copy-button', copied ? 'hi-ui-copy-button--copied' : '', className]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      type="button"
      className={classes}
      onClick={handleCopy}
      aria-label={copied ? 'Copied' : label}
      {...rest}
    >
      {children || (
        <svg
          className="hi-ui-copy-button__icon"
          viewBox="0 0 16 16"
          fill="currentColor"
          aria-hidden="true"
        >
          {copied ? (
            <path d="M13.78 4.22a.75.75 0 010 1.06l-7.25 7.25a.75.75 0 01-1.06 0L2.22 9.28a.75.75 0 011.06-1.06L6 10.94l6.72-6.72a.75.75 0 011.06 0z" />
          ) : (
            <path d="M0 6.75C0 5.784.784 5 1.75 5h1.5a.75.75 0 010 1.5h-1.5a.25.25 0 00-.25.25v7.5c0 .138.112.25.25.25h7.5a.25.25 0 00.25-.25v-1.5a.75.75 0 011.5 0v1.5A1.75 1.75 0 019.25 16h-7.5A1.75 1.75 0 010 14.25v-7.5z M5 1.75C5 .784 5.784 0 6.75 0h7.5C15.216 0 16 .784 16 1.75v7.5A1.75 1.75 0 0114.25 11h-7.5A1.75 1.75 0 015 9.25v-7.5zm1.75-.25a.25.25 0 00-.25.25v7.5c0 .138.112.25.25.25h7.5a.25.25 0 00.25-.25v-7.5a.25.25 0 00-.25-.25h-7.5z" />
          )}
        </svg>
      )}
      <span
        className={`hi-ui-copy-button__popup${copied ? ' hi-ui-copy-button__popup--show' : ''}`}
        aria-hidden="true"
      >
        {copiedMessage}
      </span>
    </button>
  );
};
